import styled from '@emotion/styled';
import { Variables } from '../base/variables/variables';

const V = Variables.ThemeLight;

export const Summary = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px 32px;
    padding: 14px 20px;
    border: 1px solid ${V.Border1};
    border-radius: 8px;
    background: ${V.Background3};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${V.TextMild};

    strong {
        margin-left: 8px;
        font-size: 15px;
        letter-spacing: 0;
        color: ${V.TextStrong};
    }
    strong.attention {
        color: ${V.AttentionInverted4};
    }
`;

export const Panels = styled.div<{ flipped: boolean }>`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 16px;
    align-items: start;
    margin-top: 16px;

    > :first-of-type {
        order: ${({ flipped }) => (flipped ? 2 : 1)};
    }
    > :last-of-type {
        order: ${({ flipped }) => (flipped ? 1 : 2)};
    }

    @media (max-width: 1100px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

export const Panel = styled.section`
    border: 1px solid ${V.Border1};
    border-radius: 8px;
    background: ${V.Background3};
    overflow: hidden;
`;

export const PanelHead = styled.div`
    display: flex;
    gap: 12px;
    padding: 18px 20px 6px;

    h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: ${V.TextStrong};
    }
    p {
        margin: 4px 0 0;
        font-size: 13px;
        color: ${V.TextMild};
    }
`;

export const Step = styled.span`
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    color: ${V.PrimaryConstant6};
    background: ${V.PrimaryMild2};
`;

export const Controls = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
`;

export const Slot = styled.div<{ width?: number; grow?: boolean }>`
    flex: ${({ grow }) => (grow ? 1 : 0)} 1 ${({ width }) => width || 160}px;
    min-width: 120px;
`;

export const SortLabel = styled.span`
    font-size: 12px;
    color: ${V.TextMild};
`;

export const ListHead = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 10px 20px 10px 56px;
    background: ${V.Background1};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${V.TextMild};
`;

export const Row = styled.div<{ selected?: boolean; selectable?: boolean }>`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 20px;
    border-top: 1px solid ${V.Border1};
    background: ${({ selected }) => (selected ? V.PrimaryMild2 : 'transparent')};
    cursor: ${({ selectable }) => (selectable ? 'pointer' : 'default')};

    :hover {
        background: ${({ selected, selectable }) => (selected ? V.PrimaryMild2 : selectable ? V.Background1 : 'transparent')};
    }
`;

export const RowMain = styled.div`
    flex: 1;
    min-width: 0;

    strong,
    span {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    strong {
        font-size: 14px;
        color: ${V.TextStrong};
    }
    span {
        margin-top: 2px;
        font-size: 12px;
        color: ${V.TextMild};
    }
`;

export const RowAmount = styled.div`
    flex: none;
    text-align: right;
    font-variant-numeric: tabular-nums;

    strong {
        display: block;
        font-size: 14px;
        color: ${V.TextStrong};
    }
    span {
        display: block;
        margin-top: 2px;
        font-size: 12px;
        color: ${V.TextMild};
    }
`;

export const RowAction = styled.div`
    flex: none;
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 96px;
    justify-content: flex-end;
`;

export const PostedTag = styled.span`
    font-size: 12px;
    font-weight: 500;
    color: ${V.SuccessInverted4};
    white-space: nowrap;
`;

export const Empty = styled.p`
    margin: 0;
    padding: 16px 20px 20px;
    font-size: 13px;
    color: ${V.TextMild};
`;

export const Foot = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 20px;
    border-top: 1px solid ${V.Border1};
    font-size: 12px;
    color: ${V.TextMild};
`;

export const Pager = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

export const NoticeSlot = styled.div`
    padding: 0 20px 12px;
`;

export const UndoBar = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
    padding: 8px 14px;
    border-radius: 8px;
    background: ${V.Background1};
    font-size: 13px;
    color: ${V.TextStrong};
`;

/* ---- Export reconciled transactions ------------------------------------------------------------ */

export const ExportBody = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    min-width: 520px;
    padding: 0 24px 20px;
`;

export const ExportLabel = styled.span`
    margin-top: 6px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${V.TextMild};
`;

export const ExportDates = styled.div`
    display: flex;
    gap: 8px;
`;

export const ExportHint = styled.p`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-size: 13px;
    color: ${V.TextMild};
`;

export const ExportPreview = styled.dl`
    display: grid;
    grid-template-columns: repeat(3, minmax(120px, 1fr));
    gap: 12px 24px;
    width: 100%;
    margin: 6px 0 0;
    padding: 12px 14px;
    border-radius: 8px;
    background: ${V.Background1};

    dt {
        font-size: 12px;
        color: ${V.TextMild};
    }
    dd {
        margin: 2px 0 0;
        font-size: 16px;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
        color: ${V.TextStrong};
    }
`;

export const ExportActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    width: 100%;
    margin-top: 8px;
`;

/** "Match on" (FE-184): a label, then one chip per quick filter, the checked ones highlighted as in Jordan's design. */
export const MatchRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 4px 20px 10px;
`;

export const MatchLabel = styled.span`
    margin-right: 4px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${V.TextMild};
`;

export const MatchChip = styled.div<{ checked: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 36px;
    padding: 0 10px;
    border: 1px solid ${({ checked }) => (checked ? V.PrimaryConstant6 : V.Border1)};
    border-radius: 8px;
    background: ${({ checked }) => (checked ? V.PrimaryMild2 : 'transparent')};
    font-size: 13px;
    color: ${V.TextStrong};
`;

export const MatchNumber = styled.div`
    width: 64px;
`;

/** The panel head's right side: "Show posted (n)", as the design places it. */
export const HeadAside = styled.div`
    margin-left: auto;
    align-self: flex-start;
`;

/** The footer line under a list: "1 unposted ERA · same amount". */
export const ListSummary = styled.p`
    margin: 0;
    padding: 10px 20px 14px;
    border-top: 1px solid ${V.Border1};
    font-size: 12px;
    color: ${V.TextMild};
`;
